import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { MoreVertical, Pencil, Copy, Trash2, Plus, Search, ShieldCheck } from "lucide-react";
import Header from "../Header";
import LeftNavBar from "../LeftNavBar";
import PageHeader from "../PageHeader";
import LoadingView from "../LoadingView";
import FailureView from "../FailureView";
import { PageHeaderName } from "../../StyledCss/styledComponents";
import { PrimaryButton } from "../../StyledCss/styledbtn";
import RoleFormModal from "./RoleFormModal";
import DeleteRoleModal from "./DeleteRoleModal";
import { getRoles, deleteRole } from "../../api/services/hroneApi";
import { parseResponseBody } from "../../util/apiResponse";
import { isProtectedRole, isHiddenRole } from "../../util/roleConstants";
import "./index.css";

const apiStatusConstants = {
  initial: "INITIAL",
  success: "SUCCESS",
  failure: "FAILURE",
  inProgress: "IN_PROGRESS",
};

const orgDetails = JSON.parse(localStorage.getItem("org"));
const rmAccentColor =
  orgDetails && orgDetails.primaryColor ? orgDetails.primaryColor : "#e86020";

const MENU_HEIGHT = 160; 

const getMenuPosition = (triggerEl) => {
  const rect = triggerEl.getBoundingClientRect();
  const openUp =
    window.innerHeight - rect.bottom < MENU_HEIGHT &&
    rect.top > window.innerHeight - rect.bottom;
  return {
    top: openUp ? "auto" : rect.bottom + 6,
    bottom: openUp ? window.innerHeight - rect.top + 6 : "auto",
    right: window.innerWidth - rect.right,
  };
};

const RoleManagementList = () => {
  const navigate = useNavigate();
  const [apiStatus, setApiStatus] = useState(apiStatusConstants.initial);
  const [roles, setRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [menu, setMenu] = useState(null); // { roleId, style }
  const [modal, setModal] = useState(null); // { type: 'create' | 'delete', role? }
  const [isSubmitting, setIsSubmitting] = useState(false);

  const menuTriggerRef = useRef(null);
  const menuPanelRef = useRef(null);

  const loadRoles = useCallback(async () => {
    setApiStatus(apiStatusConstants.inProgress);
    try {
      const response = await getRoles();
      const { ok, message, data } = await parseResponseBody(response);
      if (!ok) {
        setApiStatus(apiStatusConstants.failure);
        toast.error(message);
        return;
      }
      setRoles(data?.roles || []);
      setApiStatus(apiStatusConstants.success);
    } catch (err) {
      toast.error("Unable to reach the server. Please try again.");
      setApiStatus(apiStatusConstants.failure);
    }
  }, []);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  useEffect(() => {
    if (!menu) return undefined;
    const onMouseDown = (e) => {
      if (menuTriggerRef.current?.contains(e.target)) return;
      if (menuPanelRef.current?.contains(e.target)) return;
      setMenu(null);
    };
    const close = () => setMenu(null);
    document.addEventListener("mousedown", onMouseDown);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [menu]);

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const visibleRoles = useMemo(() => {
    // Super Admin (see roleConstants.js) is the tenant's own bootstrap
    // account — never listed here at all, not even read-only.
    const withoutHidden = roles.filter((role) => !isHiddenRole(role.name));
    const filtered = normalizedSearch
      ? withoutHidden.filter(
          (role) =>
            role.name.toLowerCase().includes(normalizedSearch) ||
            role.description?.toLowerCase().includes(normalizedSearch),
        )
      : withoutHidden;
    // No seniority number any more (roles.rank is gone), so list
    // alphabetically — stable and predictable for the reader.
    return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
  }, [roles, normalizedSearch]);

  const openEditor = (roleId) => navigate(`/role_permissions/${roleId}`);

  const toggleMenu = (roleId, triggerEl) => {
    setMenu((prev) =>
      prev?.roleId === roleId ? null : { roleId, style: getMenuPosition(triggerEl) },
    );
  };

  // Create no longer calls the backend from the modal — it only collects
  // Role Name + Description, then hands off to the Permission Configuration
  // page (CREATE mode), which is the single place the role actually gets
  // created (on Save, alongside its permissions).
  const handleCreateSubmit = ({ name, description }) => {
    const trimmedName = name.trim();
    if (!trimmedName) return { error: "Role name is required." };
    if (roles.some((r) => r.name.toLowerCase() === trimmedName.toLowerCase())) {
      return { error: "A role with this name already exists." };
    }
    setModal(null);
    navigate("/role_permissions/new", {
      state: { draftRole: { name: trimmedName, description: description.trim() } },
    });
    return {};
  };

  // Duplicate doesn't create anything either — it opens the Permission
  // Configuration page (DUPLICATE mode) pre-filled from the source role;
  // the copy only becomes a real role once the user presses Save there.
  const handleDuplicate = (role) => {
    setMenu(null);
    navigate(`/role_permissions/${role.id}/duplicate`);
  };

  const handleDeleteConfirm = async () => {
    setIsSubmitting(true);
    try {
      const response = await deleteRole(modal.role.id);
      const { ok, message } = await parseResponseBody(response);
      if (!ok) {
        toast.error(message);
        return;
      }
      toast.success(message || "Role deleted successfully.");
      setModal(null);
      setRoles((prev) => prev.filter((r) => r.id !== modal.role.id));
    } catch (err) {
      toast.error("Unable to reach the server. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderTable = () => (
    <>
      <div className="rm-intro">
        <div className="rm-intro-icon">
          <ShieldCheck size={20} />
        </div>
        <div>
          <p className="rm-intro-title">Manage role-based access</p>
          <p className="rm-intro-subtitle">
            Configure permissions once per role, then assign roles to employees.
          </p>
        </div>
      </div>

      <div className="rm-search">
        <Search size={16} className="rm-search-icon" />
        <input
          type="text"
          className="rm-search-input"
          placeholder="Search roles..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="rm-table-card">
        <div className="rm-table-scroll">
          <table className="rm-table">
            <thead>
              <tr>
                <th>Role Name</th>
                <th>Description</th>
                <th className="rm-th-center">Employees Assigned</th>
                <th className="rm-th-actions rm-th-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleRoles.length === 0 && (
                <tr>
                  <td colSpan={4} className="rm-empty">
                    {roles.length === 0
                      ? "No roles have been created yet."
                      : "No roles match your search."}
                  </td>
                </tr>
              )}
              {visibleRoles.map((role) => {
                // Gates read backend capability flags rather than the role
                // name or `type` label, so policy changes need no frontend
                // change — with one deliberate exception: Super Admin and
                // Admin are withheld here regardless of what the API allows,
                // because they administer everything else and there is
                // nothing on them worth configuring (see roleConstants.js).
                const isProtected = isProtectedRole(role.name);
                const canEdit = !!role.can_edit && !isProtected;
                const canDelete = !!role.can_delete && !isProtected;
                const canDuplicate = !!role.can_duplicate;
                const isMenuOpen = menu?.roleId === role.id;
                return (
                  <tr key={role.id}>
                    <td className="rm-role-name">{role.name}</td>
                    <td className="rm-role-desc">{role.description || "—"}</td>
                    <td className="rm-th-center">{role.employees_assigned}</td>
                    <td>
                      <div className="rm-actions-cell rm-actions-cell-center">
                        <div
                          className="rm-menu-wrap"
                          ref={isMenuOpen ? menuTriggerRef : null}
                        >
                          <button
                            type="button"
                            className="rm-menu-trigger"
                            aria-label="More actions"
                            onClick={(e) => toggleMenu(role.id, e.currentTarget)}
                          >
                            <MoreVertical size={16} />
                          </button>
                          {isMenuOpen &&
                            createPortal(
                              <div className="rm-menu" ref={menuPanelRef} style={menu.style}>
                                {/* Not offered at all rather than opened
                                    read-only — the row still lists the role. */}
                                {canEdit && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setMenu(null);
                                      openEditor(role.id);
                                    }}
                                  >
                                    <Pencil size={14} /> Edit Permissions
                                  </button>
                                )}
                                {canDuplicate && (
                                  <button type="button" onClick={() => handleDuplicate(role)}>
                                    <Copy size={14} /> Duplicate Role
                                  </button>
                                )}
                                {canDelete && (
                                  <span
                                    className={
                                      role.employees_assigned > 0
                                        ? "rm-tooltip-wrap"
                                        : undefined
                                    }
                                    data-tooltip={
                                      role.employees_assigned > 0
                                        ? `Move ${role.employees_assigned} employee${
                                            role.employees_assigned > 1 ? "s" : ""
                                          } to another role to delete this one`
                                        : undefined
                                    }
                                  >
                                    <button
                                      type="button"
                                      className="rm-menu-danger"
                                      disabled={role.employees_assigned > 0}
                                      onClick={() => {
                                        setMenu(null);
                                        setModal({ type: "delete", role });
                                      }}
                                    >
                                      <Trash2 size={14} /> Delete Role
                                    </button>
                                  </span>
                                )}
                              </div>,
                              document.body,
                            )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  const renderContent = () => {
    switch (apiStatus) {
      case apiStatusConstants.inProgress:
      case apiStatusConstants.initial:
        return <LoadingView />;
      case apiStatusConstants.failure:
        return <FailureView onClickRetry={loadRoles} />;
      case apiStatusConstants.success:
        return renderTable();
      default:
        return null;
    }
  };

  return (
    <div>
      <Header />
      <div className="d-flex">
        <LeftNavBar />
        <div className="page-content-wrapper">
          <PageHeader>
            <PageHeaderName>Role Management</PageHeaderName>
            <PrimaryButton width="160px" onClick={() => setModal({ type: "create" })}>
              <Plus size={16} style={{ verticalAlign: "-3px", marginRight: 6 }} />
              Create New Role
            </PrimaryButton>
          </PageHeader>

          <div
            className="rm-container"
            style={{ "--rm-accent": rmAccentColor }}
          >
            {renderContent()}
          </div>
        </div>
      </div>

      {modal?.type === "create" && (
        <RoleFormModal
          onCancel={() => setModal(null)}
          onSubmit={handleCreateSubmit}
          isSubmitting={isSubmitting}
        />
      )}
      {modal?.type === "delete" && (
        <DeleteRoleModal
          role={modal.role}
          onCancel={() => setModal(null)}
          onConfirm={handleDeleteConfirm}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
};

export default RoleManagementList;
